import { Breadcrumb, Layout, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import { IDictionary } from '~utilities/IDictionary';
import { loansDrawdownsListAction } from '~/Loans/actions';
import { loansDrawdownsSelector } from '~/Loans/selectors';
import { currencyFormatter } from '~utilities/formatters';
import { warehousesListAction } from '~Warehouses/actions';
import IWarehouse from '~Api/Warehouse/IWarehouse';
import { warehousesSelector } from '~Warehouses/selectors';
import IWarehouseLoan from '~Api/Warehouse/IWarehouseLoan';
import dayjs from 'dayjs';
import WarehouseTypeEnum from '~Api/Warehouse/WarehouseTypeEnum';
import { authCurrentUserSelector } from '~Auth/selectors';
import IAuthUser from '~Auth/IAuthUser';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';

export default function Drawdowns(): ReactElement {
    const currentUser: IAuthUser = useSelector(authCurrentUserSelector);
    const loans: IDictionary<ILoan> = useSelector(loansDrawdownsSelector);
    const warehouses: IDictionary<IWarehouse> = useSelector(warehousesSelector);

    const showWarehouses: boolean = currentUser.permissions.includes(PermissionsEnum.Warehouses);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!loans) {
            dispatch(loansDrawdownsListAction());
        }
    }, [
        dispatch,
        loans,
    ]);

    useEffect(() => {
        if (showWarehouses && !warehouses) {
            dispatch(warehousesListAction());
        }
    }, [
        currentUser,
        dispatch,
        showWarehouses,
        warehouses,
    ]);

    if (!loans || (showWarehouses && !warehouses)) {
        return (
            <Layout className='loan-drawdowns'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to='/loans'>Loans</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Drawdowns</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Typography.Title level={2}>Drawdowns</Typography.Title>
                        <Spin />
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    const columns: ColumnsType<ILoan> = [
        {
            dataIndex: 'code',
            fixed: 'left',
            render: (code: string, loan: ILoan) => <Link to={`/loans/${loan.uuid}`}>{code}</Link>,
            title: 'Code',
            width: 180,
        },
        {
            dataIndex: 'name',
            render: (name: string, loan: ILoan) => <Link to={`/loans/${loan.uuid}`}>{name}</Link>,
            title: 'Name',
            width: 300,
        },
        {
            dataIndex: 'amount',
            render: (amount: number) => currencyFormatter.format(amount),
            title: 'Loan Amount',
            width: 150,
        },
        {
            dataIndex: 'endDate',
            render: (endDate: string) => dayjs(endDate).format('Do MMMM YYYY'),
            title: 'End Date',
            width: 180,
        },
        {
            dataIndex: 'retainedFunds',
            render: (retainedFunds: number) => retainedFunds ? currencyFormatter.format(retainedFunds) : '-',
            title: 'Retained Funds',
            width: 150,
        },
        {
            dataIndex: 'retainedContingency',
            render: (retainedContingency: number) => retainedContingency ? currencyFormatter.format(retainedContingency) : '-',
            title: 'Retained Contingency',
            width: 180,
        },
        {
            defaultSortOrder: 'descend',
            render: (loan: ILoan) => (loan.retainedFunds + loan.retainedContingency) > 0 ? currencyFormatter.format(loan.retainedFunds + loan.retainedContingency) : '-',
            sorter: (a: ILoan, b: ILoan) => (a.retainedFunds + a.retainedContingency) - (b.retainedFunds + b.retainedContingency),
            title: 'Total Retained',
            width: 150,
        },
        {
            render: (loan: ILoan) => loan.drawdownCount > 0 ? <Link to={`/loans/${loan.uuid}/drawdowns`}>{loan.drawdownCount}</Link> : '-',
            title: 'Count',
            width: 70,
        },
    ];

    if (showWarehouses) {
        columns.push({
            fixed: 'right',
            render: (loan: ILoan): ReactNode => {
                const activeWarehouseLoans: IWarehouseLoan[] = _.filter(loan.warehouseLoans, (warehouseLoan: IWarehouseLoan) => warehouseLoan.balancePrincipal < 0);

                const activeWarehouses: IWarehouse[] = _.map(activeWarehouseLoans, (warehouseLoan: IWarehouseLoan) => warehouses[warehouseLoan.warehouseUuid]);

                const nonFplActiveWarehouses: IWarehouse[] = _.filter(activeWarehouses, (warehouse: IWarehouse) => WarehouseTypeEnum.Fpl !== warehouse.type);

                return nonFplActiveWarehouses[0].name;
            },
            title: 'Warehouse',
            width: 210,
        });
    }

    return (
        <Layout className='loans'>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/loans'>Loans</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Drawdowns</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Typography.Title level={2}>Drawdowns</Typography.Title>
                    <Table
                        columns={columns}
                        dataSource={_.values(loans)}
                        pagination={false}
                        rowKey='uuid'
                        scroll={{ x: 1 }}
                        size='middle'
                    />
                </Layout.Content>
            </Layout>
        </Layout>
    );
}
